import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Card, CardBody, Button, Input, Table, Badge, Spinner, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { Fn_FillListData, Fn_DeleteData } from '../../store/Functions';
import { API_WEB_URLS } from '../../constants/constAPI';
import AddEdit_LedgerMaster from '../Masters/AddEdit_LedgerMaster';
import toastr from 'toastr';
import 'toastr/build/toastr.css';

const LedgerListH = () => {
    const dispatch = useDispatch();
    const [state, setState] = useState({
        ledgerData: [],
        loading: true
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalId, setModalId] = useState(0);
    const [deletingId, setDeletingId] = useState(null);

    const fetchDataList = useCallback(async () => {
        try {
            setState(prev => ({ ...prev, loading: true }));
            await Fn_FillListData(
                dispatch,
                setState,
                'ledgerData',
                `${API_WEB_URLS?.MASTER || ''}/0/token/OtherLedgers/Id/0`
            );
        } catch (error) {
            console.error('Error fetching hidden ledger list:', error);
        } finally {
            setState(prev => ({ ...prev, loading: false }));
        }
    }, [dispatch]);

    useEffect(() => {
        fetchDataList();
    }, [fetchDataList]);

    const handleEdit = (id) => {
        setModalId(id);
        setModalOpen(true);
    };

    const handleAddNew = () => {
        setModalId(0);
        setModalOpen(true);
    };

    const handleModalSuccess = () => {
        setModalOpen(false);
        fetchDataList();
    };

    const handleDelete = async (id, ledgerName) => {
        const confirmed = window.confirm(`Are you sure you want to delete ledger "${ledgerName || id}"? This action cannot be undone.`);
        if (confirmed) {
            try {
                setDeletingId(id);
                const API_URL_Delete = `${API_WEB_URLS.MASTER}/0/token/DeletePartyAccount`;
                const response = await Fn_DeleteData(dispatch, () => {}, id, API_URL_Delete, true);
                const msg = response?.data?.message || response?.message || "";
                if (response && response.status === 200 && (msg.toLowerCase().includes("record deleted") || msg.toLowerCase().includes("deleted"))) {
                    toastr.success(`Ledger "${ledgerName || id}" deleted successfully.`);
                } else if (response && (response.status === 409 || response.status === 404)) {
                    toastr.error("Cannot delete: This ledger is used in vouchers or contracts!");
                }
                await fetchDataList();
            } catch (error) {
                console.error("Error deleting hidden ledger:", error);
                toastr.error("Failed to delete ledger. It may be used in vouchers or contracts.");
            } finally {
                setDeletingId(null);
            }
        }
    };

    const filteredData = useMemo(() => {
        let data = state.ledgerData;
        if (statusFilter !== 'all') {
            data = data.filter(item => statusFilter === 'active' ? item.IsActive : !item.IsActive);
        }
        if (!searchQuery) return data;
        const q = searchQuery.toLowerCase();
        return data.filter(item =>
            String(item.Name).toLowerCase().includes(q)
        );
    }, [state.ledgerData, searchQuery, statusFilter]);

    return (
        <Card>
            <CardBody>
                <div style={{ overflowX: "auto", overflowY: "hidden", paddingBottom: "10px", marginBottom: "15px" }}>
                    <div className="d-flex align-items-center gap-2" style={{ minWidth: "max-content" }}>
                        <div className="d-flex align-items-center bg-white border rounded p-1">
                            <Button
                                color={statusFilter === 'all' ? 'primary' : 'light'}
                                size="sm"
                                className="px-3"
                                onClick={() => setStatusFilter('all')}
                            >
                                All
                            </Button>
                            <Button
                                color={statusFilter === 'active' ? 'primary' : 'light'}
                                size="sm"
                                className="px-3 mx-1"
                                onClick={() => setStatusFilter('active')}
                            >
                                Active
                            </Button>
                            <Button
                                color={statusFilter === 'inactive' ? 'primary' : 'light'}
                                size="sm"
                                className="px-3"
                                onClick={() => setStatusFilter('inactive')}
                            >
                                Inactive
                            </Button>
                        </div>
                        <Input
                            type="text"
                            placeholder="Search by company name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            bsSize="sm"
                            style={{ width: "250px" }}
                        />
                        <div className="d-flex gap-2">
                            <Button color="success" size="sm" onClick={handleAddNew} style={{ whiteSpace: "nowrap" }}>
                                <i className="bx bx-plus me-1"></i> Add Ledger
                            </Button>
                            <Button color="primary" size="sm" onClick={fetchDataList} title="Refresh Data">
                                <i className="bx bx-refresh"></i>
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Responsive Table Container */}
                <div className="table-responsive" style={{ maxHeight: '60vh', border: '1px solid #EFF2F7', borderRadius: '4px' }}>
                    <Table hover className="align-middle table-nowrap mb-0 table-sm text-nowrap">
                        <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr>
                                <th style={{ backgroundColor: '#f8f9fa' }}>Name</th>
                                <th className="text-center" style={{ backgroundColor: '#f8f9fa' }}>Status</th>
                                <th className="text-center" style={{ backgroundColor: '#f8f9fa', width: '100px' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.loading ? (
                                <tr>
                                    <td colSpan="3" className="text-center py-5">
                                        <Spinner color="primary" size="sm" className="me-2" /> Loading Ledgers...
                                    </td>
                                </tr>
                            ) : filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan="3" className="text-center py-4 text-muted">No ledgers found.</td>
                                </tr>
                            ) : (
                                filteredData.map((item, idx) => (
                                    <tr key={idx}>
                                        <td className="fw-bold" style={{ whiteSpace: 'normal', minWidth: '200px' }}>{item.Name}</td>
                                        <td className="text-center">
                                            <Badge color={item.IsActive ? 'success' : 'danger'} pill>
                                                {item.IsActive ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td className="text-center">
                                            <Button
                                                color="soft-info"
                                                size="sm"
                                                onClick={() => handleEdit(item.Id)}
                                                title="Edit Ledger"
                                                className="me-1"
                                            >
                                                <i className="bx bx-edit font-size-14"></i>
                                            </Button>
                                            <Button
                                                color="soft-danger"
                                                size="sm"
                                                onClick={() => handleDelete(item.Id, item.Name)}
                                                title="Delete Ledger"
                                                disabled={deletingId === item.Id}
                                            >
                                                {deletingId === item.Id ? (
                                                    <Spinner size="sm" style={{ width: '12px', height: '12px' }} />
                                                ) : (
                                                    <i className="bx bx-trash font-size-14 text-danger"></i>
                                                )}
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </CardBody>

            <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="xl" scrollable>
                <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
                    {modalId > 0 ? "Edit Ledger" : "Add New Ledger"}
                </ModalHeader>
                <ModalBody>
                    {modalOpen && (
                        <AddEdit_LedgerMaster
                            isModal={true}
                            modalId={modalId}
                            defaultGroup={36}
                            onSuccess={handleModalSuccess}
                            onCancel={() => setModalOpen(false)}
                        />
                    )}
                </ModalBody>
            </Modal>
        </Card>
    );
};

export default LedgerListH;
